import { AccessTime } from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    Alert,
    Box,
    Chip,
    FormControlLabel,
    Grid,
    Radio,
    RadioGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { locationsAPI } from 'api/locationsAPI';
import { JourneyType } from 'app/constants/Booking/BookingConstants';
import { BookingSummary } from 'components/Bookings/BookingSummary/BookingSummary';
import DisplayError from 'components/Common/DisplayError/DisplayError';
import SectionHeader from 'components/Common/SectionHeader';
import Checkbox from 'components/RouteWizard/Checkbox';
import Form from 'components/RouteWizard/Form';
import { StepComponentProps } from 'components/RouteWizard/RouteWizard';
import { Select } from 'components/RouteWizard/Select';
import { useWizard } from 'components/RouteWizard/Wizard';
import { addDays, isAfter } from 'date-fns';
import useFormState from 'hooks/useFormState';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import AdditionalServices from './AdditionalServices';
import AddressSearch from './AddressSearch';
import DateTimeSelect from './LegacyDateTimeSelect';
import Stops from './Stops';
import defaultValues from './defaultValues';
import schema from './schema';
import { asDirectedHours, waitTimes } from './timeOptions';

const JourneyPlan = ({ initialValues, onSuccess }: StepComponentProps) => {
  const { data } = useWizard()

  const [formMethods, setFormMethods] = useState<UseFormReturn | null>(null)

  const journeyTypeName = useFormState('journeyTypeName', formMethods)
  const pickup = useFormState('outbound.pickup', formMethods)
  const dropOff =
    useFormState('outbound.dropOff', formMethods) ||
    initialValues?.outbound?.dropOff
  const outboundWhen =
    useFormState('outbound.when', formMethods) || initialValues?.outbound?.when
  const inboundWhen = useFormState('inbound.when', formMethods)
  const isImmediateJourney = useFormState('isImmediateJourney', formMethods)
  const stops = useFormState('additionalStops', formMethods, true)
  // const [enabled, setEnabled] = useState(false)

  const preview = {
    ...data.preview,
    pickup,
    dropOff,
    when: outboundWhen,
    additionalStops: stops,
    isImmediateJourney:
      journeyTypeName == JourneyType.ONE_WAY && isImmediateJourney,
  }

  // Temporary diagnostic for onward-journey pre-population.
  useEffect(() => {
    console.log('[Onward:plan] data.preview:', data?.preview)
    console.log('[Onward:plan] data.account:', data?.account)
    console.log(
      '[Onward:plan] preview.companyName:',
      preview.companyName,
      'passengers:',
      preview.passengers,
    )
  }, [data?.preview, data?.account, preview.companyName, preview.passengers])

  const checkDateAndTime = (data?: any) => {
    //if immediate journey - all good
    if (data?.isImmediateJourney) {
      return true
    }
    //if not immediate journey then it needs to be +30 minutes from now
    const outboundWhenTimeZone = DateTime.fromJSDate(
      data.outbound.when,
    ).setZone(data.outbound.timeZone, { keepLocalTime: true })
    const timeZoneNow = DateTime.now().setZone(data.outbound.timeZone)
    const diff = outboundWhenTimeZone.diff(timeZoneNow, ['minutes']).toObject()
    if ((diff.minutes || 0) < 30) {
      formMethods?.setError('outbound.when', {
        type: 'custom',
        message: `Time must be after ${timeZoneNow
          .plus({
            minutes: 30,
          })
          .toLocaleString(
            DateTime.DATETIME_MED,
          )}, or select One Way and ASAP for an immediate pick up`,
      })
      return false
    }

    const inboundWhenTimeZone = DateTime.fromJSDate(data.inbound.when).setZone(
      data.outbound.timeZone,
      { keepLocalTime: true },
    )
    const outboundTimeZone = DateTime.fromJSDate(data.outbound.when).setZone(
      data.outbound.timeZone,
      { keepLocalTime: true },
    )
    const diffInbound = inboundWhenTimeZone
      .diff(outboundTimeZone, ['minutes'])
      .toObject()

    if (
      (diffInbound?.minutes || 0) < 30 &&
      journeyTypeName === JourneyType.RETURN
    ) {
      formMethods?.setError('inbound.when', {
        type: 'custom',
        message: `Please select a time after pickup`,
      })
      return false
    }
    return true
  }

  const onSubmit = (data: any) => {
    if (!checkDateAndTime(data)) {
      return false
    }
    onSuccess({
      plan: {
        ...data,
        inbound:
          data.journeyTypeName == JourneyType.RETURN
            ? {
                ...data.inbound,
                pickup: data.outbound?.dropOff,
                dropOff: data.outbound?.pickup,
              }
            : null,
      },
      preview,
    })
  }

  const countryCode = formMethods?.getValues('outbound.pickup.countryCode')
  const latAndLong =
    formMethods?.getValues('outbound.pickup.latitude') !== undefined &&
    formMethods?.getValues('outbound.pickup.longitude') !== undefined
  useQuery({
    queryKey: ['getTimeZoneQuote'],
    queryFn: async () => {
      return locationsAPI.getTimeZone(
        formMethods?.getValues('outbound.pickup.latitude'),
        formMethods?.getValues('outbound.pickup.longitude'),
      )
    },
    enabled: Boolean((!countryCode || countryCode !== 'GBR') && latAndLong),
    staleTime: 0,
    onSuccess: (data) => {
      formMethods?.setValue('outbound.timeZone', data?.data.timeZone)
    },
  })

  useEffect(() => {
    if (formMethods?.getValues('outbound.pickup.countryCode') === 'GBR') {
      console.log('pickup is GBR')
      formMethods.setValue('outbound.timeZone', 'Europe/London')
    } else {
      console.log('pickup is not GBR')
    }
  }, [formMethods?.getValues('outbound.pickup.countryCode')])

  const setOutboundToday = () => {
    const today = new Date()
    if (outboundWhen) {
      today.setHours(outboundWhen?.getHours(), outboundWhen?.getMinutes())
    }
    formMethods?.setValue('outbound.when', today)
  }

  const setOutboundTomorrow = () => {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    if (outboundWhen) {
      tomorrow.setHours(outboundWhen?.getHours(), outboundWhen?.getMinutes())
    }
    formMethods?.setValue('outbound.when', tomorrow)
  }

  const setInboundSameDay = () => {
    const sameDay = outboundWhen ? new Date(outboundWhen) : new Date()
    if (inboundWhen) {
      sameDay.setHours(inboundWhen?.getHours(), inboundWhen?.getMinutes())
    }
    formMethods?.setValue('inbound.when', sameDay)
  }

  const setInboundNextDay = () => {
    const nextDay = outboundWhen ? new Date(outboundWhen) : new Date()
    nextDay.setDate(nextDay.getDate() + 1)
    if (inboundWhen) {
      nextDay.setHours(inboundWhen?.getHours(), inboundWhen?.getMinutes())
    }
    formMethods?.setValue('inbound.when', nextDay)
  }

  const populateFixedRoute = (fixedRoute: any) => {
    formMethods?.setValue('outbound.pickup', fixedRoute.pickup)
    formMethods?.setValue('outbound.dropOff', fixedRoute.dropOff)
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={7}>
        <Form
          schema={schema}
          ref={setFormMethods}
          onSubmit={onSubmit}
          defaultValues={{
            ...defaultValues,
            ...initialValues,
            outbound: {
              ...defaultValues?.outbound,
              ...initialValues?.outbound,
            },
            inbound: {
              ...defaultValues?.inbound,
              ...initialValues?.inbound,
            },
          }}
          formId="journeyPlan"
          showControls={false}
          sx={{ float: 'right', marginRight: '40px' }}
        >
          <AddressSearch
            name="outbound.pickup"
            key={JSON.stringify(formMethods?.getValues('outbound.pickup'))}
            id="pick-up"
            label="Pick up"
            clientId={data?.account?.clientId}
            populateFixedRoute={populateFixedRoute}
          />
          {formMethods?.formState?.errors?.outbound?.pickup && (
            <DisplayError
              message={formMethods?.formState?.errors?.outbound?.pickup.message}
              sx={{
                marginTop: '-10px',
                marginLeft: '125px',
              }}
            />
          )}

          <AddressSearch
            name="outbound.dropOff"
            id="drop-off"
            key={JSON.stringify(formMethods?.getValues('outbound.dropOff'))}
            label="Drop Off"
            clientId={data?.account?.clientId}
            populateFixedRoute={populateFixedRoute}
          />
          {formMethods?.formState?.errors?.outbound?.dropOff && (
            <DisplayError
              message={
                formMethods?.formState?.errors?.outbound?.dropOff.message
              }
              sx={{
                marginTop: '-10px',
                marginLeft: '125px',
              }}
            />
          )}

          {journeyTypeName === JourneyType.AS_DIRECTED && (
            <SectionHeader
              subtitle="Note: Drop Off is not mandatory for an hourly trip"
              containerStyles={{
                alignSelf: 'left',
                marginLeft: '120px',
              }}
            />
          )}

          <Stops name="additionalStops" clientId={data?.account?.clientId} />

          <Controller
            control={formMethods?.control}
            name={'journeyTypeName'}
            render={({ field: { ...fieldProps } }) => {
              return (
                <RadioGroup
                  aria-label="journeytype"
                  {...fieldProps}
                  sx={{
                    backgroundColor: '#f4f4f4',
                    fontSize: '20px',
                    color: '#878787',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-evenly',
                    marginTop: '20px',
                    padding: '10px 20px',
                  }}
                >
                  <FormControlLabel
                    value={JourneyType.ONE_WAY}
                    control={<Radio />}
                    label="One Way"
                  />
                  <FormControlLabel
                    value={JourneyType.FLEXIBLE}
                    control={<Radio />}
                    label={
                      <Box
                        sx={{
                          display: 'flex',
                          gap: '5px',
                          alignItems: 'center',
                        }}
                      >
                        <Typography>Flexible</Typography>
                        {!isAfter(
                          new Date(),
                          addDays(new Date('2025-08-15'), 30),
                        ) && (
                          <Chip
                            label={'new'}
                            size="small"
                            color="success"
                            sx={{ position: 'relative', top: '-10px' }}
                          ></Chip>
                        )}
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value={JourneyType.RETURN}
                    control={<Radio />}
                    label="Return"
                  />
                  <FormControlLabel
                    value={JourneyType.AS_DIRECTED}
                    control={<Radio />}
                    label="Hourly Trip"
                  />
                </RadioGroup>
              )
            }}
          />
          {journeyTypeName === JourneyType.FLEXIBLE && (
            <Alert
              icon={<AccessTime />}
              color="info"
              sx={{ marginTop: '20px' }}
            >
              Flexible bookings let you request your car when you’re ready. Just
              give us your expected departure time - it’s okay if you’re early
              or late. Your booking stays active for 12 hours after that time.
            </Alert>
          )}
          <Box
            component="div"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              gap: '20px',
            }}
          >
            <Grid container justifyContent={'center'}>
              <Grid item xs={6} sx={{ paddingRight: '10px' }}>
                <DateTimeSelect
                  namePrefix="outbound"
                  title={
                    journeyTypeName === JourneyType.FLEXIBLE
                      ? 'expected departure'
                      : 'out'
                  }
                  leftQuickButtonLabel="today"
                  leftOnClick={setOutboundToday}
                  rightQuickButtonLabel="tomorrow"
                  rightOnClick={setOutboundTomorrow}
                  disabled={
                    isImmediateJourney &&
                    journeyTypeName === JourneyType.ONE_WAY
                  }
                />
              </Grid>
              {journeyTypeName === JourneyType.RETURN && (
                <Grid item xs={6} sx={{ paddingLeft: '10px' }}>
                  <DateTimeSelect
                    namePrefix="inbound"
                    title="return"
                    leftQuickButtonLabel="same day"
                    leftOnClick={setInboundSameDay}
                    rightQuickButtonLabel="next day"
                    rightOnClick={setInboundNextDay}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
          {journeyTypeName === JourneyType.AS_DIRECTED && (
            <Grid container justifyContent="center">
              <Grid item xs={6} sx={{ paddingRight: '10px' }}>
                <Box
                  display="flex"
                  sx={{ marginTop: '20px' }}
                  justifyContent="flex-start"
                >
                  <SectionHeader
                    title={'Hourly Trip'}
                    containerStyles={{
                      alignSelf: 'center',
                    }}
                  />
                  <Tooltip title="Select the amount of time for trip and your driver will remain on standby for the duration.">
                    <InfoOutlinedIcon
                      fontSize="small"
                      sx={{ paddingLeft: '5px' }}
                    />
                  </Tooltip>
                </Box>
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    gap: '10px',
                  }}
                >
                  <Select
                    name={`directedJourneyTime`}
                    options={
                      asDirectedHours?.reduce<Record<string, string>>(
                        (acc, item) => {
                          acc[item.value] = item.label
                          return acc
                        },
                        {},
                      ) || {}
                    }
                    sx={{ flexGrow: 1, marginRight: '10px' }}
                  />
                </Box>
              </Grid>
            </Grid>
          )}
          {journeyTypeName === JourneyType.WAIT_AND_RETURN && (
            <Grid item xs={6}>
              <SectionHeader
                title={'Waiting Time'}
                containerStyles={{
                  alignSelf: 'center',
                  marginTop: '20px',
                }}
              />
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  gap: '10px',
                }}
              >
                <Select
                  name={`waitHours`}
                  options={
                    waitTimes
                      ?.filter((option) => option?.type === 'hours')
                      ?.reduce<Record<string, string>>((acc, item) => {
                        acc[item.label] = item.value
                        return acc
                      }, {}) || {}
                  }
                  label="Hours"
                  sx={{ flexGrow: 1 }}
                />

                <Select
                  name={`waitMinutes`}
                  options={
                    waitTimes
                      ?.filter((option) => option?.type === 'minutes')
                      ?.reduce<Record<string, string>>((acc, item) => {
                        acc[item.label] = item.value
                        return acc
                      }, {}) || {}
                  }
                  label="Minutes"
                  sx={{ flexGrow: 1 }}
                />
              </Box>
            </Grid>
          )}

          {journeyTypeName === JourneyType.ONE_WAY && (
            <Grid container justifyContent={'center'}>
              <Grid item xs={6} sx={{ paddingRight: '10px' }}>
                <Checkbox name="isImmediateJourney" label="ASAP" />
              </Grid>
            </Grid>
          )}
          <Grid container justifyContent="center">
            <Grid
              item
              xs={journeyTypeName === JourneyType.RETURN ? 12 : 6}
              sx={
                journeyTypeName === JourneyType.RETURN
                  ? undefined
                  : { paddingRight: '10px' }
              }
            >
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  gap: '20px',
                }}
              >
                <AdditionalServices namePrefix="outbound" />
                {journeyTypeName === JourneyType.RETURN && (
                  <AdditionalServices namePrefix="inbound" />
                )}
              </Box>
            </Grid>
          </Grid>
        </Form>
      </Grid>
      <Grid item xs={5}>
        <BookingSummary bookingPreview={preview} formId="journeyPlan" />
      </Grid>
    </Grid>
  )
}

export default JourneyPlan
